import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

export default function Categorias({ onMudanca }) {
  const [categorias, setCategorias] = useState([]);
  const [novaCategoria, setNovaCategoria] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [openId, setOpenId] = useState(null);

  const iniciarEdicaoCard = (categoria, id) => {
    setIsEditing(true);
    setEditingId(id);
    setNovaCategoria(categoria.descricao ?? categoria);
    setMostrarForm(false);
    setError('');
  };

  const cancelarEdicaoCard = () => {
    setIsEditing(false);
    setEditingId(null);
    setNovaCategoria('');
    setError('');
  };

  const apiUrls = [
    `${BASE_URL}/CategoriaProduto`,
    `${BASE_URL}/CategoriaProduto`
  ];


  const apiGet = async () => {
    const headers = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    for (const url of apiUrls) {
      try {
        const resp = await fetch(url, { headers });
        if (resp.ok) {
          const data = await resp.json();
          return data;
        }
      } catch (err) {
        // try next
      }
    }
    throw new Error('Nenhuma URL de API respondeu para GET /CategoriaProduto');
  };

  const apiPost = async (body) => {
    const headers = { 'Content-Type': 'application/json' };
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    for (const url of apiUrls) {
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        });
        if (resp.ok) {
          try { return await resp.json(); } catch { return null; }
        }
      } catch (err) {
        // try next
      }
    }
    throw new Error('Nenhuma URL de API respondeu para POST /CategoriaProduto');
  };

  const apiPut = async (id, body) => {
    // try same base urls but append id
    for (const base of apiUrls) {
      const url = base.endsWith('/CategoriaProduto') ? `${base}/${id}` : `${base}/${id}`;
      try {
        console.log('Tentando PUT em:', url);
        // ensure payload includes id (backend Edit requires categoria.Id == id)
        const payload = Object.assign({}, body, { id });
        const headers = { 'Content-Type': 'application/json' };
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(url, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
        if (resp.ok) {
          // some endpoints return 204 NoContent
          if (resp.status === 204) return null;
          try { return await resp.json(); } catch { return null; }
        } else {
          // try to surface backend error
          let text = '';
          try { text = await resp.text(); } catch {}
          throw new Error(`PUT ${url} falhou: ${resp.status} ${text}`);
        }
      } catch (err) {
        console.warn('PUT failed for', url, err.message);
        // try next base URL
      }
    }
    throw new Error('Nenhuma URL de API respondeu para PUT /CategoriaProduto/{id}');
  };

  const apiDelete = async (id) => {
    const headers = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    for (const base of apiUrls) {
      const url = `${base}/${id}`;
      try {
        const resp = await fetch(url, { method: 'DELETE', headers });

        if (resp.ok || resp.status === 204) {
          return { success: true };
        }

        // Parse JSON error from backend
        let payload = null;
        try { payload = await resp.json(); } catch { payload = null; }

        const err = new Error(payload?.mensagem || `Erro ao deletar categoria (${resp.status})`);
        err.tipo = payload?.tipo || 'ERRO_GERAL';
        err.statusCode = resp.status;
        throw err;
      } catch (err) {
        if (err.statusCode != null) throw err; // backend error, propagate
        // network/cert error, try next URL
      }
    }
    const err = new Error('Nenhuma URL de API respondeu para DELETE /CategoriaProduto/{id}');
    err.tipo = 'ERRO_GERAL';
    throw err;
  };

  const excluirCategoria = async () => {
    if (!editingId) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiDelete(editingId);
      if (res.success) {
        const list = await apiGet();
        setCategorias(Array.isArray(list) ? list : []);
        setMostrarForm(false);
        setNovaCategoria('');
        setIsEditing(false);
        setEditingId(null);
        onMudanca?.();
        return;
      }
    } catch (err) {
      if (err.tipo === 'PRODUTOS_ASSOCIADOS') {
        setError(err.message);
      } else {
        console.error('Erro ao deletar categoria:', err.message);
        setError('Erro ao deletar categoria. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const list = await apiGet();
        if (mounted) setCategorias(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Erro ao carregar categorias:', err.message);
        if (mounted) setError('Erro ao carregar categorias');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, []);

  const adicionarCategoria = async () => {
    if (!novaCategoria.trim()) return;
    setLoading(true);
    setError('');
    try {
      if (isEditing && editingId) {
        // perform update
        try {
          await apiPut(editingId, { descricao: novaCategoria.trim() });
          const list = await apiGet();
          setCategorias(Array.isArray(list) ? list : []);
          setNovaCategoria('');
          setMostrarForm(false);
          setIsEditing(false);
          setEditingId(null);
          onMudanca?.();
        } catch (err) {
          console.error('Erro ao atualizar categoria:', err.message);
          setError('Erro ao atualizar categoria');
        }
      } else {
        // create new category
        const created = await apiPost({ descricao: novaCategoria.trim() });
        if (created && created.id) {
          setCategorias(prev => [created, ...prev]);
        } else {
          const list = await apiGet();
          setCategorias(Array.isArray(list) ? list : []);
        }
        setNovaCategoria('');
        setMostrarForm(false);
        onMudanca?.();
      }
    } catch (err) {
      console.error('Erro ao criar/atualizar categoria:', err.message);
      setError('Erro ao criar/atualizar categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      adicionarCategoria();
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    }}>
      <MobileAddListener onOpen={() => { setMostrarForm(true); setIsEditing(false); setNovaCategoria(''); setEditingId(null); }} />
      {/* Botão Nova Categoria */}
      <button
        type="button"
        onClick={() => {
          const opening = !mostrarForm;
          setMostrarForm(opening);
          if (opening) {
            setIsEditing(false);
            setNovaCategoria('');
            setEditingId(null);
          }
        }}
        style={{
          padding: '12px 15px',
          backgroundColor: 'rgb(235, 133, 0)',
          border: '1px solid rgb(235, 133, 0)',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          color: 'white',
          textAlign: 'left',
          transition: 'all 0.2s'
        }}
        onMouseEnter={e => {
          e.target.style.backgroundColor = 'rgb(208, 116, 0)';
          e.target.style.borderColor = 'rgb(208, 116, 0)';
        }}
        onMouseLeave={e => {
          e.target.style.backgroundColor = 'rgb(235, 133, 0)';
          e.target.style.borderColor = 'rgb(235, 133, 0)';
        }}
      >
        Nova Categoria
      </button>

      {/* Formulário acordeão */}
      {mostrarForm && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #ddd',
          borderRadius: '6px',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <input
            type="text"
            value={novaCategoria}
            onChange={(e) => setNovaCategoria(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite o nome da categoria"
            style={{
              width: 'calc(100% - 24px)',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              marginBottom: '10px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            autoFocus
            disabled={loading}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={adicionarCategoria}
              style={{
                backgroundColor: 'rgb(235, 133, 0)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={e => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
              onMouseLeave={e => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
              disabled={loading}
            >
              {loading ? 'Enviando...' : (isEditing ? 'Editar' : 'Adicionar')}
            </button>
            {isEditing && (
              <button
                onClick={excluirCategoria}
                type="button"
                style={{ padding: '8px 12px', borderRadius: 6, border: 'none', background: '#d32f2f', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                disabled={loading}
              >
                Deletar
              </button>
            )}
            <button
              type="button"
              onClick={() => { setMostrarForm(false); setNovaCategoria(''); setError(''); setIsEditing(false); setEditingId(null); }}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', color: 'rgb(235, 133, 0)', fontWeight: 600 }}
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        </div>
      )}

      {/* Lista de categorias adicionadas */}
      {loading && !categorias.length ? (
        <div style={{ padding: 12, color: '#666' }}>Carregando categorias...</div>
      ) : (!loading && categorias.length === 0 ? (
        <div style={{ padding: 12, color: '#666' }}>Nenhuma categoria cadastrada.</div>
      ) : (
        <>
          {categorias.map((categoria, idx) => {
            const id = categoria.id ?? idx;
            const isOpen = openId === id;
            const isEditMode = isEditing && editingId === id;
            return (
              <div
                key={id}
                style={{ display: 'block', marginBottom: 8 }}
                onMouseEnter={() => setOpenId(id)}
                onMouseLeave={() => setOpenId(null)}
              >
                <div>
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #ddd',
                      borderRadius: isOpen ? '8px 8px 0 0' : 8,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      textAlign: 'left',
                      transition: 'all 0.12s ease-in-out',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>{categoria.descricao ?? categoria}</span>
                    <span style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms' }}>▸</span>
                  </button>
                </div>

                {isEditMode ? (
                  <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                      <button
                        type="button"
                        onClick={cancelarEdicaoCard}
                        style={{ background: '#bbbbbb', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 10px', fontWeight: 600, cursor: 'pointer' }}
                        disabled={loading}
                      >
                        Fechar
                      </button>
                    </div>
                    <input
                      value={novaCategoria}
                      onChange={(e) => setNovaCategoria(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid #ccc', marginBottom: 10 }}
                      disabled={loading}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={adicionarCategoria}
                        style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 12px', fontWeight: 600, cursor: 'pointer' }}
                        disabled={loading || !novaCategoria.trim()}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={excluirCategoria}
                        style={{ backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 12px', fontWeight: 600, cursor: 'pointer' }}
                        disabled={loading}
                      >
                        Deletar
                      </button>
                      <button
                        type="button"
                        onClick={cancelarEdicaoCard}
                        style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 4, padding: '8px 12px', color: '#333', fontWeight: 600, cursor: 'pointer' }}
                        disabled={loading}
                      >
                        Cancelar
                      </button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                      <button
                        type="button"
                        onClick={cancelarEdicaoCard}
                        style={{ background: '#bbbbbb', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 10px', fontWeight: 600, cursor: 'pointer' }}
                        disabled={loading}
                      >
                        Fechar
                      </button>
                    </div>
                    {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                  </div>
                ) : (
                  isOpen && (
                    <div
                      role="button"
                      tabIndex={0}
                      style={{
                        backgroundColor: 'rgb(235, 133, 0)',
                        color: '#fff',
                        padding: '6px 12px',
                        border: '1px solid rgb(235, 133, 0)',
                        borderTop: 'none',
                        borderRadius: '0 0 15px 15px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer'
                      }}
                      onClick={() => iniciarEdicaoCard(categoria, id)}
                    >
                      Editar
                    </div>
                  )
                )}
              </div>
            );
          })}

          
        </>
      ))}

      {/* CSS para animação */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
